import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BookOpen,
  ChevronDown,
  Layers,
  Loader2,
  Mail,
  Pencil,
  Plus,
  ShieldCheck,
  Star,
  Trash2,
  X,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { ledgerApi, billingApi } from '../../api';
import { PageSearchBar } from '../common/PageSearchBar';
import { canManageLedgers, getLedgerList, setLedgerList, switchLedger, type LedgerPublic } from '../../lib/ledger';
import { getCurrentUser, USER_CHANGED_EVENT } from '../../lib/permissions';
import { hydrateTenantWorkspace } from '../../lib/cloudSync';
import { cardValueClass, CARD_VALUE_BASE } from '../../lib/responsiveCardValue';
import { useTranslation } from '../../lib/i18n';

type LedgerFormState = {
  name: string;
  loginEmail: string;
  loginPassword: string;
  includeMain: boolean;
  extraLedgerIds: string[];
};

const emptyForm = (): LedgerFormState => ({
  name: '',
  loginEmail: '',
  loginPassword: '',
  includeMain: true,
  extraLedgerIds: [],
});

type ConsolidationOptions = { mainName: string; others: LedgerPublic[] };

function ConsolidationFields({
  options,
  form,
  setForm,
}: {
  options: ConsolidationOptions;
  form: LedgerFormState;
  setForm: React.Dispatch<React.SetStateAction<LedgerFormState>>;
}) {
  const toggleExtra = (id: string) =>
    setForm((f) => ({
      ...f,
      extraLedgerIds: f.extraLedgerIds.includes(id) ? f.extraLedgerIds.filter((x) => x !== id) : [...f.extraLedgerIds, id],
    }));
  return (
    <div className="rounded-lg border border-zinc-200 bg-zinc-50/80 p-3">
      <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-zinc-500">
        <Layers className="h-3.5 w-3.5" />
        Consolidated reports
      </p>
      <p className="mb-3 text-[11px] text-zinc-500">
        Shown only on this ledger&apos;s Reports page as combined totals. Transactions are never moved or shared, and
        customer and loan details stay private.
      </p>
      <label className="flex items-start gap-2 text-sm text-zinc-800">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={form.includeMain}
          onChange={(e) => setForm((f) => ({ ...f, includeMain: e.target.checked }))}
        />
        <span>
          Include Main Account <span className="text-zinc-500">({options.mainName})</span>
        </span>
      </label>
      {options.others.length > 0 ? (
        <div className="mt-3 space-y-2 border-t border-zinc-200 pt-3">
          <p className="text-[11px] font-medium text-zinc-600">Also include these ledgers (off unless you select them):</p>
          {options.others.map((l) => (
            <label key={l.id} className="flex items-center gap-2 text-sm text-zinc-800">
              <input type="checkbox" checked={form.extraLedgerIds.includes(l.id)} onChange={() => toggleExtra(l.id)} />
              {l.name}
              {l.isDisabled ? <span className="text-[11px] text-zinc-400">(disabled, skipped)</span> : null}
            </label>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function StatusBadge({ ledger }: { ledger: LedgerPublic }) {
  const { t } = useTranslation();
  if (ledger.isDefault) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
        <Star className="h-3 w-3" aria-hidden />
        {t('status.default')}
      </span>
    );
  }
  if (ledger.isDisabled) {
    return (
      <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-700">{t('status.disabled')}</span>
    );
  }
  return (
    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">{t('status.active')}</span>
  );
}

function LedgerFormModal({
  title,
  subtitle,
  form,
  setForm,
  onClose,
  onSubmit,
  loading,
  passwordHint,
  consolidation,
}: {
  title: string;
  subtitle?: string;
  form: LedgerFormState;
  setForm: React.Dispatch<React.SetStateAction<LedgerFormState>>;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  loading: boolean;
  passwordHint: string;
  /** Present only for branch ledgers — the Main Account never shows consolidated reports. */
  consolidation?: ConsolidationOptions;
}) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-[120] flex items-end justify-center bg-zinc-950/40 p-0 backdrop-blur-sm sm:items-center sm:p-4">
      <div
        className="app-card w-full max-h-[92dvh] overflow-y-auto shadow-xl sm:max-w-md sm:rounded-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ledger-form-title"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-zinc-200 bg-white px-4 py-3 sm:px-5">
          <div className="min-w-0 pr-3">
            <h3 id="ledger-form-title" className="text-sm font-semibold text-zinc-900 sm:text-base">
              {title}
            </h3>
            {subtitle ? <p className="mt-0.5 text-xs text-zinc-500">{subtitle}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-zinc-500 transition hover:bg-zinc-100"
            aria-label={t('common.close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="app-card-inner space-y-4">
          <div>
            <label className="app-label" htmlFor="ledger-name">
              {t('settingsForm.ledgerName')}
            </label>
            <input
              id="ledger-name"
              className="app-field"
              placeholder="e.g. City office"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              autoFocus
              required
            />
          </div>

          <div className="rounded-lg border border-zinc-200 bg-zinc-50/80 p-3">
            <p className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-zinc-500">
              <ShieldCheck className="h-3.5 w-3.5" />
              {t('settingsForm.deskLogin')}
            </p>
            <div className="space-y-3">
              <div>
                <label className="app-label" htmlFor="ledger-email">
                  {t('settingsForm.email')}
                </label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                  <input
                    id="ledger-email"
                    type="email"
                    className="app-field pl-9"
                    placeholder="staff@yourcompany.com"
                    value={form.loginEmail}
                    onChange={(e) => setForm((f) => ({ ...f, loginEmail: e.target.value }))}
                    autoComplete="off"
                  />
                </div>
              </div>
              <div>
                <label className="app-label" htmlFor="ledger-password">
                  {t('login.password')}
                </label>
                <input
                  id="ledger-password"
                  type="password"
                  className="app-field"
                  placeholder={passwordHint}
                  value={form.loginPassword}
                  onChange={(e) => setForm((f) => ({ ...f, loginPassword: e.target.value }))}
                  autoComplete="new-password"
                />
              </div>
            </div>
          </div>

          {consolidation ? <ConsolidationFields options={consolidation} form={form} setForm={setForm} /> : null}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button type="button" className="app-btn-secondary w-full sm:w-auto" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="app-btn-primary inline-flex w-full items-center justify-center gap-2 sm:w-auto" disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function LedgerSettings() {
  const { t } = useTranslation();
  const [user, setUser] = useState(getCurrentUser);
  const canManage = canManageLedgers(user);
  const [ledgers, setLedgers] = useState<LedgerPublic[]>(getLedgerList);
  const [search, setSearch] = useState('');
  const [fetching, setFetching] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState<LedgerFormState>(emptyForm);
  const [createLoading, setCreateLoading] = useState(false);
  const [editing, setEditing] = useState<LedgerPublic | null>(null);
  const [editForm, setEditForm] = useState<LedgerFormState>(emptyForm);
  const [editLoading, setEditLoading] = useState(false);
  const [maxLedgers, setMaxLedgers] = useState(0);
  const [ledgerLimitReached, setLedgerLimitReached] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [res, statusRes] = await Promise.all([ledgerApi.list(), billingApi.status().catch(() => null)]);
      setLedgers(res.ledgers);
      setLedgerList(res.ledgers);
      const max = statusRes?.maxLedgers ?? 0;
      setMaxLedgers(max);
      setLedgerLimitReached(Boolean(statusRes?.ledgerLimitReached));
    } catch (e) {
      if (e instanceof Error && e.message) toast.error(e.message);
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => {
    const syncUser = () => setUser(getCurrentUser());
    syncUser();
    window.addEventListener(USER_CHANGED_EVENT, syncUser);
    return () => window.removeEventListener(USER_CHANGED_EVENT, syncUser);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return ledgers;
    return ledgers.filter(
      (l) => l.name.toLowerCase().includes(q) || (l.loginEmail || '').toLowerCase().includes(q)
    );
  }, [ledgers, search]);

  const stats = useMemo(
    () => ({
      total: ledgers.length,
      active: ledgers.filter((l) => !l.isDisabled).length,
      withLogin: ledgers.filter((l) => l.hasLogin).length,
    }),
    [ledgers]
  );

  const createLedger = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = createForm.name.trim();
    const trimmedEmail = createForm.loginEmail.trim();
    const trimmedPassword = createForm.loginPassword.trim();
    if (!trimmedName) {
      toast.error('Enter ledger name.');
      return;
    }
    if (trimmedEmail && !trimmedPassword) {
      toast.error('Enter a login password for this ledger email.');
      return;
    }
    if (!trimmedEmail && trimmedPassword) {
      toast.error('Enter a login email when setting a password.');
      return;
    }
    if (ledgerLimitReached) {
      toast.error(
        maxLedgers > 0
          ? `Ledger limit reached (${maxLedgers} on your plan). Upgrade to add more branch books.`
          : 'Ledger limit reached on your plan.'
      );
      return;
    }
    setCreateLoading(true);
    try {
      const payload: { name: string; loginEmail?: string; loginPassword?: string } = { name: trimmedName };
      if (trimmedEmail && trimmedPassword) {
        payload.loginEmail = trimmedEmail;
        payload.loginPassword = trimmedPassword;
      }
      const res = await ledgerApi.create(payload);
      setCreateForm(emptyForm());
      setShowCreate(false);
      await refresh();
      const newId = res.ledger?.id;
      if (newId) {
        await switchLedger(newId, { onSwitch: hydrateTenantWorkspace });
        toast.success(`Ledger "${trimmedName}" created and activated.`);
      } else {
        toast.success('Ledger created.');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not create ledger.');
    } finally {
      setCreateLoading(false);
    }
  };

  const startEdit = (l: LedgerPublic) => {
    setEditing(l);
    setEditForm({
      name: l.name,
      loginEmail: l.loginEmail || '',
      loginPassword: '',
      includeMain: l.reportConsolidation?.includeMain !== false,
      extraLedgerIds: [...(l.reportConsolidation?.ledgerIds || [])],
    });
    setShowCreate(false);
  };

  const cancelEdit = () => {
    setEditing(null);
    setEditForm(emptyForm());
  };

  const saveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    const trimmedName = editForm.name.trim();
    const trimmedEmail = editForm.loginEmail.trim().toLowerCase();
    const trimmedPassword = editForm.loginPassword.trim();
    if (!trimmedName) {
      toast.error('Enter ledger name.');
      return;
    }

    const payload: Parameters<typeof ledgerApi.update>[1] = { name: trimmedName };
    if (!editing.isDefault) {
      payload.reportConsolidation = { includeMain: editForm.includeMain, ledgerIds: editForm.extraLedgerIds };
    }
    const hadLogin = editing.hasLogin;
    const prevEmail = (editing.loginEmail || '').toLowerCase();
    const emailChanged = trimmedEmail !== prevEmail;

    if (hadLogin && !trimmedEmail) {
      payload.loginEmail = null;
    } else if (trimmedEmail) {
      if (emailChanged && !trimmedPassword) {
        toast.error('Enter a password for the desk login email.');
        return;
      }
      if (emailChanged) payload.loginEmail = trimmedEmail;
      if (trimmedPassword) payload.loginPassword = trimmedPassword;
    } else if (trimmedPassword && hadLogin) {
      payload.loginPassword = trimmedPassword;
    }

    setEditLoading(true);
    try {
      await ledgerApi.update(editing.id, payload);
      await refresh();
      cancelEdit();
      toast.success('Ledger updated.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update ledger.');
    } finally {
      setEditLoading(false);
    }
  };

  const setDefault = async (id: string) => {
    try {
      await ledgerApi.setDefault(id);
      await refresh();
      toast.success('Default ledger updated.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed.');
    }
  };

  const toggleDisabled = async (l: LedgerPublic) => {
    if (l.isDefault && !l.isDisabled) {
      toast.error('Set another ledger as default before disabling.');
      return;
    }
    try {
      await ledgerApi.update(l.id, { isDisabled: !l.isDisabled });
      await refresh();
      toast.success(l.isDisabled ? 'Ledger enabled.' : 'Ledger disabled.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed.');
    }
  };

  const deleteLedger = async (l: LedgerPublic) => {
    if (l.isDefault) {
      toast.error('Cannot delete the default ledger.');
      return;
    }
    const count = l.recordCount ?? 0;
    if (count > 0) {
      toast.error(
        `Cannot delete "${l.name}" — it has ${count} record(s). Use Disable to hide it instead.`,
        { autoClose: 6000 }
      );
      return;
    }
    if (!window.confirm(`Delete ledger "${l.name}"?\n\nThis only works when the ledger has no finance, loan, or other data. This cannot be undone.`)) {
      return;
    }
    try {
      await ledgerApi.remove(l.id);
      if (editing?.id === l.id) cancelEdit();
      await refresh();
      toast.success(`Ledger "${l.name}" deleted.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not delete this ledger.');
    }
  };

  return (
    <div className="space-y-5 pb-2">
      <div className="rounded-xl border border-indigo-100 bg-gradient-to-br from-indigo-50/80 to-white p-4">
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600">
            <BookOpen className="h-4 w-4" aria-hidden />
          </span>
          <div className="min-w-0 text-sm leading-relaxed text-zinc-600">
            <p className="font-medium text-zinc-800">{t('settingsForm.ledgerTitle')}</p>
            <p className="mt-1 text-xs sm:text-sm">
              {t('settingsForm.ledgerHint')}
              {maxLedgers > 0 ? (
                <span className="mt-1 block font-medium text-indigo-700">
                  Plan limit: {stats.total} / {maxLedgers} branch book{maxLedgers === 1 ? '' : 's'}.
                </span>
              ) : null}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        {[
          { label: t('common.total'), value: stats.total },
          { label: t('status.active'), value: stats.active },
          { label: t('settingsForm.deskLogin'), value: stats.withLogin },
        ].map((s) => (
          <div key={s.label} className="rounded-xl border border-zinc-200 bg-white px-3 py-3 text-center shadow-sm">
            <p className={`${CARD_VALUE_BASE} ${cardValueClass(String(s.value), 'compact')} text-zinc-900`}>
              {s.value}
            </p>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500 sm:text-xs">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <PageSearchBar
          value={search}
          onChange={setSearch}
          placeholder={t('common.search')}
          className="max-w-full flex-1"
        />
        {canManage ? (
          <button
            type="button"
            disabled={ledgerLimitReached}
            onClick={() => {
              if (ledgerLimitReached) {
                toast.error(
                  maxLedgers > 0
                    ? `Ledger limit reached (${maxLedgers} on your plan). Upgrade to add more.`
                    : 'Ledger limit reached on your plan.'
                );
                return;
              }
              setShowCreate((v) => !v);
              cancelEdit();
            }}
            className="app-btn-primary inline-flex w-full shrink-0 items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
          >
            <Plus className="h-4 w-4" />
            {t('settings.ledgers')}
            <ChevronDown className={`h-4 w-4 transition-transform ${showCreate ? 'rotate-180' : ''}`} />
          </button>
        ) : null}
      </div>

      {canManage && showCreate ? (
        <form onSubmit={createLedger} className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4 space-y-3">
          <p className="text-sm font-semibold text-zinc-900">{t('settingsForm.createLedger')}</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="app-label">{t('settingsForm.ledgerName')}</label>
              <input
                className="app-field"
                placeholder="e.g. City office"
                value={createForm.name}
                onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
                required
              />
            </div>
            <div>
              <label className="app-label">{t('settingsForm.deskLogin')}</label>
              <input
                className="app-field"
                type="email"
                placeholder={t('common.select')}
                value={createForm.loginEmail}
                onChange={(e) => setCreateForm((f) => ({ ...f, loginEmail: e.target.value }))}
              />
            </div>
            <div>
              <label className="app-label">{t('login.password')}</label>
              <input
                className="app-field"
                type="password"
                placeholder="Any password you choose"
                value={createForm.loginPassword}
                onChange={(e) => setCreateForm((f) => ({ ...f, loginPassword: e.target.value }))}
              />
            </div>
          </div>
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <button
              type="button"
              className="app-btn-secondary w-full sm:w-auto"
              onClick={() => {
                setShowCreate(false);
                setCreateForm(emptyForm());
              }}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="app-btn-primary inline-flex w-full items-center justify-center gap-2 sm:w-auto"
              disabled={createLoading}
            >
              {createLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              {t('settingsForm.createLedger')}
            </button>
          </div>
        </form>
      ) : null}

      <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-zinc-200 bg-zinc-50/80 px-4 py-3">
          <div>
            <h3 className="text-sm font-semibold text-zinc-900">{t('settings.ledgers')}</h3>
            <p className="text-[11px] text-zinc-500 lg:hidden">{t('common.swipeTable')}</p>
          </div>
          <span className="rounded-full border border-zinc-200 bg-white px-2.5 py-0.5 text-xs font-medium text-zinc-600">
            {filtered.length} of {ledgers.length}
          </span>
        </div>

        {fetching ? (
          <div className="flex flex-col items-center justify-center gap-2 py-14 text-sm text-zinc-500">
            <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
            {t('common.loading')}
          </div>
        ) : ledgers.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 px-4 py-12 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-zinc-100 text-zinc-400">
              <BookOpen className="h-7 w-7" />
            </span>
            <div>
              <p className="text-sm font-semibold text-zinc-800">{t('settings.ledgers')}</p>
              <p className="mt-1 max-w-xs text-xs text-zinc-500">
                {canManage ? t('settingsForm.ledgerHint') : t('common.readOnly')}
              </p>
            </div>
            {canManage ? (
              <button type="button" className="app-btn-primary inline-flex items-center gap-2" onClick={() => setShowCreate(true)}>
                <Plus className="h-4 w-4" />
                {t('settings.ledgers')}
              </button>
            ) : null}
          </div>
        ) : filtered.length === 0 ? (
          <p className="py-10 text-center text-sm text-zinc-500">{t('common.noMatch')}</p>
        ) : (
          <div className="app-table-wrap -mx-px rounded-none border-0 border-t border-zinc-200">
            <table className="app-table min-w-[42rem]">
              <thead>
                <tr>
                  <th className="min-w-[10rem]">{t('settings.ledgers')}</th>
                  <th className="min-w-[12rem]">{t('settingsForm.deskLogin')}</th>
                  <th className="min-w-[5rem]">{t('common.records')}</th>
                  <th className="min-w-[6rem]">{t('common.status')}</th>
                  {canManage ? <th className="min-w-[14rem] text-right">{t('common.actions')}</th> : null}
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100 bg-white">
                {filtered.map((l) => (
                  <tr key={l.id} className={`hover:bg-zinc-50/80 ${editing?.id === l.id ? 'bg-indigo-50/40' : ''}`}>
                    <td>
                      <span className="inline-flex items-center gap-2 font-medium text-zinc-900">
                        <BookOpen className="h-4 w-4 shrink-0 text-zinc-400" aria-hidden />
                        {l.name}
                      </span>
                    </td>
                    <td className="text-zinc-600">{l.hasLogin ? l.loginEmail : '—'}</td>
                    <td className="tabular-nums text-zinc-600">{l.recordCount ?? '—'}</td>
                    <td>
                      <StatusBadge ledger={l} />
                    </td>
                    {canManage ? (
                      <td>
                        <div className="flex flex-nowrap items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => startEdit(l)}
                            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-zinc-700 hover:bg-zinc-100"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                            {t('common.edit')}
                          </button>
                          {!l.isDefault ? (
                            <>
                              <button
                                type="button"
                                onClick={() => setDefault(l.id)}
                                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-amber-800 hover:bg-amber-50"
                              >
                                <Star className="h-3.5 w-3.5" />
                                {t('common.select')}
                              </button>
                              <button
                                type="button"
                                onClick={() => toggleDisabled(l)}
                                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-zinc-700 hover:bg-zinc-100"
                              >
                                {l.isDisabled ? t('status.active') : t('status.disabled')}
                              </button>
                              <button
                                type="button"
                                onClick={() => deleteLedger(l)}
                                disabled={(l.recordCount ?? 0) > 0}
                                title={
                                  (l.recordCount ?? 0) > 0
                                    ? `${l.recordCount} ${t('common.records')}`
                                    : t('common.delete')
                                }
                                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-40"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                                {t('common.delete')}
                              </button>
                            </>
                          ) : null}
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!canManage ? (
        <p className="rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3 text-center text-sm text-zinc-500">
          {t('common.readOnly')}
        </p>
      ) : null}

      {editing && canManage ? (
        <LedgerFormModal
          title={t('common.edit')}
          subtitle={editing.name}
          form={editForm}
          setForm={setEditForm}
          onClose={cancelEdit}
          onSubmit={saveEdit}
          loading={editLoading}
          passwordHint={editing.hasLogin ? 'Leave blank to keep current password' : 'Any password you choose'}
          consolidation={
            editing.isDefault
              ? undefined
              : {
                  mainName: ledgers.find((l) => l.isDefault)?.name || 'Main Account',
                  others: ledgers.filter((l) => !l.isDefault && l.id !== editing.id),
                }
          }
        />
      ) : null}
    </div>
  );
}
